import { cn } from "@gymflow/helpers";
import { AnimatePresence, motion } from "framer-motion";
import {
  createContext,
  ReactNode,
  useCallback,
  useState
} from "react";
import { createPortal } from "react-dom";


export const ModalContext = createContext<{
  setModal: (modal: ReactNode) => void;
  hide: () => void;
  stackModal: (modal: ReactNode) => void;
  popModal: () => void;
  modalStack: ReactNode[] | null;
}>({
  setModal: () => {
    throw Error("ModalContext not initialized.");
  },
  hide: () => {
    throw Error("ModalContext not initialized.");
  },
  stackModal: () => {
    throw Error("ModalContext not initialized.");
  },
  popModal: () => {
    throw Error("ModalContext not initialized.");
  },
  modalStack: null,
});

export function ModalProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [modalStack, setModalStack] = useState<ReactNode[] | null>(null);
  const hide = useCallback(() => {
    setModalStack(null);
  }, []);

  const setModal = useCallback(
    (modal: ReactNode) => {
      setModalStack([modal]);
    },
    [setModalStack],
  );

  const stackModal = useCallback(
    (modal: ReactNode) => {
      setModalStack((prev) => {
        if (prev) {
          return [...prev, modal];
        } else {
          return [modal];
        }
      });
    },
    [setModalStack],
  );

  const popModal = useCallback(() => {
    setModalStack((prev) => {
      if (prev) {
        return prev.slice(0, prev.length - 1);
      } else {
        return null;
      }
    });
  }, [setModalStack]);
  const m = modalStack?.[modalStack.length - 1];
  return (
    <ModalContext.Provider
      value={{
        setModal,
        stackModal,
        popModal,
        hide,
        modalStack,
      }}
    >
      <AnimatePresence>{m}</AnimatePresence>
      {children}
    </ModalContext.Provider>
  );
}

export function ModalWrapper({
  onCancel,
  children,
  className,
  overlayClassName
}: {
  onCancel?: () => void;
  children: ReactNode;
  className?: string;
  overlayClassName?: string;
}) {
  return createPortal(
    <div className={cn("fixed inset-0 z-[80000] flex flex-col items-center justify-center p-2 max-lg:flex-col-reverse max-lg:justify-start", overlayClassName)}>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="absolute inset-0 bg-gray-900/70 backdrop-blur-[2px] transition-opacity"
        onClick={onCancel}
      />

      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.95 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={cn(
          "flex dark:border-gray-800 dark:border rounded-lg bg-gray-0 p-8 sm:p-6 shadow-xl dark:bg-gray-950 z-10 justify-center w-fit flex-col max-h-full",
          className,
        )}
      >
        {children}
      </motion.div>
    </div>,
    document.body,
  );
}
